/**
 * 초기 비밀번호 강제 변경 게이트. 화면을 그리지 않고(null) 라우트가 바뀔 때마다 판정만 한다.
 *
 * `pages/_app.tsx` 의 `isMounted` 블록 안에서 `SessionManager` 옆에 앱 전체에 한 번만 올려 둔다.
 * 로그인 응답의 `needPasswordChange` 가 sessionStorage 에 "true" 로 저장돼 있는 동안,
 * `/password-setup` 이 아닌 모든 경로를 `/password-setup` 으로 되돌린다(강제 변경이 끝나기 전에
 * 허용되는 것은 변경 화면과 로그아웃뿐이라는 기획 요구). 판정 조건 네 가지:
 *  1. `accessToken` 이 있다 — 없으면 `/password-setup` 의 `useAuthGuard`(토큰 없음 → `/login`)와
 *     서로 밀어내며 핑퐁이 나므로 게이트는 물러난다.
 *  2. `needPasswordChange === "true"`.
 *  3. `isLoggingOut !== "true"` — `useLogout` 이 `router.replace("/login")` 을 먼저 하고
 *     `sessionStorage.clear()` 를 나중에 하므로, 그 사이에 게이트가 로그아웃을 되돌리지 않게 한다.
 *  4. 현재 경로가 `/password-setup` 이 아니다.
 *
 * ★ 하드 이동과의 경합: `pages/index.tsx` 는 TEACHER 를 `window.location.replace("/teacher")` 로,
 *   `pages/api/axiosInstance.ts` 는 401 토큰 갱신 후 `window.location.reload()` 로 보낸다. 둘 다
 *   이 게이트의 `router.replace` 를 취소시킬 수 있지만, 하드 이동 뒤 앱이 다시 마운트되면 이 effect 가
 *   재진입 경로에서 다시 판정하므로 결과적으로 `/password-setup` 에 도달한다. 이 전제 위에서 동작한다.
 * ★ `play.tsx`·`play1.tsx`(비로그인 공개 체험 페이지, 가드 금지 규칙)는 플래그가 없으면 조건 2 에서
 *   빠지므로 영향이 없다. 플래그가 있는 계정은 로그인 상태이므로 공개 페이지 예외 대상이 아니다.
 * ★ static export: sessionStorage 는 effect 안에서만 읽는다. 저장소 접근이 막힌 환경은 조용히 건너뛴다.
 */
import { useEffect } from "react";
import { useRouter } from "next/router";

/** 강제 변경 화면 경로. `pages/password-setup.tsx` 와 `_app.tsx` TITLE_RULES 가 같은 값을 쓴다. */
export const PASSWORD_SETUP_PATH = "/password-setup";

/** 플래그가 켜진 로그인 사용자를 `/password-setup` 으로 되돌리는 컴포넌트. 항상 null 을 반환한다. */
export default function PasswordChangeGate() {
  const router = useRouter();
  const pathname = router.pathname;

  useEffect(() => {
    if (pathname === PASSWORD_SETUP_PATH) return;

    let shouldRedirect = false;
    try {
      const hasToken = Boolean(sessionStorage.getItem("accessToken"));
      const needChange =
        sessionStorage.getItem("needPasswordChange") === "true";
      const isLoggingOut = sessionStorage.getItem("isLoggingOut") === "true";
      shouldRedirect = hasToken && needChange && !isLoggingOut;
    } catch {
      /* 저장소 접근 불가 환경(프라이빗 모드 등)은 게이트를 건너뛴다 */
    }

    if (shouldRedirect) {
      router.replace(PASSWORD_SETUP_PATH);
    }
  }, [pathname, router]);

  return null;
}
